import React, {
  useContext,
  useCallback,
  useState,
  useMemo,
  useEffect,
  ReactNode,
} from 'react';
import { makeStyles } from 'tss-react/mui';
import shortid from 'shortid';
import { getAttribute } from '../../../../../../../Model/avenyModelUtils';
import { ModelContext } from '../../../../../../../Model/ModelContext/ModelContext';
import { useIsSmallScreen } from '../../../../../../../Hooks/useFullscreenView';
import { AUTOMATICALLY_ADDED_VALUE_PREFIX } from '../../../FakturaDel/Components/FakturaDelContextManager/FakturaDelContextManager';
import { EDAutoCompleteEdit } from '../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import Dialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import EditableAttribute from '../../../../../../../Components/Attributes/EditableAttribute/EditableAttribute';
import { EDInputGroup } from '../../../../../../../BaseComponents/InputGroup/EDInputGroup';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { AllCertifiers_db_LRAttestratt_All } from '../../../FakturaDel/Components/FakturaAttestHandleCertifiersModal/Queries/_QueryTypes/AllCertifiers';
import { AttestantQuery_node_TEDLRAttestant__Associations_Reserver_All } from '../../Queries/_QueryTypes/AttestantQuery';
import { AttestantType__Associations_Attestratt_All } from '../../Queries/_QueryTypes/AttestantType';
import {
  BusinessDataFormContext,
  IBusinessDataFormContext,
} from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import {
  FormUpdateAssociatonType,
  getFormUpdateValue,
} from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import {
  mergeBusinessDataFormValues,
  parseText,
} from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { ReservContext } from '../ReserverContext/ReserverContext';

interface IProps {
  attestratter: AttestantType__Associations_Attestratt_All[];
  certifiers: AllCertifiers_db_LRAttestratt_All[];
  dialogOpen: boolean;
  editReserve: boolean;
  id: string;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const useStyles = makeStyles()(theme => ({
  autocomplete: {
    boxSizing: 'border-box',
    padding: `0 ${theme.spacing(1)}`,
  },
  label: {
    padding: `0 ${theme.spacing(1)}`,
  },
}));

const getDisabledReasons = (
  standin: AttestantQuery_node_TEDLRAttestant__Associations_Reserver_All,
  attestrattID: string | null,
  attestratter: AttestantType__Associations_Attestratt_All[]
) => {
  let disabledReasons: ReactNode[] = [];

  const attetratterDisabled = attestratter?.length > 1 ? !attestrattID : true;

  if (standin?.DATUM_TOM) {
    if (
      new Date(standin?.DATUM_TOM).toLocaleDateString('sv-SE') <
        new Date(standin?.DATUM_FROM).toLocaleDateString('sv-SE') &&
      standin?.DATUM_FROM
    ) {
      disabledReasons.push(
        <>
          Till datum måste vara större än från datum <br />
        </>
      );
    }
  }

  if (!standin?.DATUM_FROM) {
    disabledReasons.push(
      <>
        Du måste ange ett från datum <br />
      </>
    );
  }

  if (!standin?.NOTERING) {
    disabledReasons.push(
      <>
        Du måste ange en notering <br />
      </>
    );
  }

  if (!standin?.Reserv) {
    disabledReasons.push(
      <>
        Du måste välja en reserv <br />
      </>
    );
  }

  if (!attestrattID) {
    disabledReasons = ['Du måste välja ett attestområde'];
  }

  if (
    !standin?.DATUM_FROM &&
    !standin?.NOTERING &&
    !standin?.Reserv &&
    attetratterDisabled
  ) {
    disabledReasons = ['Du måste fylla i alla obligatoriska fält'];
  }

  return disabledReasons;
};

const Reserv: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { classes } = useStyles();
  const modelContext = useContext(ModelContext);
  const reservContext = useContext(ReservContext);
  const businessDataFormContext = useContext(BusinessDataFormContext);
  const isSmallScreen = useIsSmallScreen();
  const [certifier, setCertifier] = useState<
    AllCertifiers_db_LRAttestratt_All | undefined
  >();
  const [standin, setStandin] =
    useState<AttestantQuery_node_TEDLRAttestant__Associations_Reserver_All>(
      {} as AttestantQuery_node_TEDLRAttestant__Associations_Reserver_All
    );

  const updateFunction = useCallback(
    (dataformContext: IBusinessDataFormContext) => (newValue: any) => {
      dataformContext?.update([
        {
          operation: 'Update_Values',
          associationName: 'Reserver',
          id: props.id,
          values: newValue,
        },
      ]);
    },
    [props.id]
  );

  const dataMergedWithChanges = useMemo(
    () =>
      mergeBusinessDataFormValues(
        parseText(businessDataFormContext?.data),
        parseText(businessDataFormContext?.formState.values ?? {})
      ),
    [businessDataFormContext?.data, businessDataFormContext?.formState.values]
  );

  const reservData = useMemo(
    () =>
      dataMergedWithChanges?._Associations?.Reserver?.filter(
        (reserv: any) => reserv.id === props.id
      )[0],
    [dataMergedWithChanges?._Associations?.Reserver, props.id]
  );

  const attestReservModel =
    modelContext.avenyCollectionMap.get('lrattestrattreserv');

  const dateFromAttribute = getAttribute(
    'LRAttestrattReserv',
    '',
    'DATUM_FROM',
    modelContext.avenyCollectionMap
  );

  const dateToAttribute = getAttribute(
    'LRAttestrattReserv',
    '',
    'DATUM_TOM',
    modelContext.avenyCollectionMap
  );

  const noteAttribute = getAttribute(
    'LRAttestrattReserv',
    '',
    'NOTERING',
    modelContext.avenyCollectionMap
  );

  const reservAttribute = getAttribute(
    'LRAttestrattReserv',
    '',
    'Reserv',
    modelContext.avenyCollectionMap
  );

  if (
    !dateFromAttribute ||
    !dateToAttribute ||
    !noteAttribute ||
    !reservAttribute ||
    !attestReservModel
  ) {
    throw new Error('Problem att ladda in Attribute');
  }

  useEffect(() => {
    setStandin(reservData);
  }, [reservData]);

  useEffect(() => {
    if (props.attestratter?.length === 1) {
      reservContext.setAttestrattID(props.attestratter?.[0]?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.attestratter]);

  const findCertifiers = useCallback(
    (certifier: AllCertifiers_db_LRAttestratt_All) => {
      const myCertifierAreas =
        dataMergedWithChanges?._Associations?.Attestratt?.map(
          (attestratt: any) => attestratt._Associations.Attestomrade
        );

      const attestGrupp:
        | AttestantType__Associations_Attestratt_All
        | undefined = dataMergedWithChanges?._Associations?.Attestratt?.find(
        (attestratt: AttestantType__Associations_Attestratt_All) =>
          attestratt.id === reservContext.attestrattID
      );

      return attestGrupp == null
        ? myCertifierAreas?.length === 1
          ? myCertifierAreas[0].ATTEST_GRUPP_ID === certifier.Attestgruppid
          : true
        : certifier.Attestgruppid === attestGrupp.Attestgruppid;
    },
    [dataMergedWithChanges, reservContext]
  );

  const reservFieldDisabled =
    reservContext.attestrattID == null &&
    !reservContext.attestrattID &&
    props.attestratter?.length !== 1;

  const isSaveButtonDisabled =
    getDisabledReasons(standin, reservContext.attestrattID, props.attestratter)
      .length !== 0;

  return (
    businessDataFormContext && (
      <Dialog
        open={reservContext.dialogOpen}
        onClose={() => {
          businessDataFormContext?.formStateDispatch({
            type: 'CANCEL',
          });
          reservContext.setDialogOpen(false);
        }}
        title={
          reservContext.editStandin
            ? 'Redigera frånvaro'
            : 'Registrera frånvaro'
        }
        footerActions={[
          {
            label: 'Avbryt',
            onClick: () => {
              businessDataFormContext?.formStateDispatch({
                type: 'CANCEL',
              });
              reservContext.setDialogOpen(false);
            },
          },
          {
            label: 'Spara',
            onClick: () => {
              if (reservContext.editStandin != null) {
                businessDataFormContext?.saveForm();
              } else {
                const id =
                  AUTOMATICALLY_ADDED_VALUE_PREFIX +
                  'reserv' +
                  shortid.generate();

                const updateObject: FormUpdateAssociatonType = {
                  operation: 'Add_Values',
                  associationName: 'Reserver',
                  id: id,
                  values: {
                    DATUM_FROM: standin?.DATUM_FROM ?? '',
                    DATUM_TOM: standin?.DATUM_TOM ?? '',
                    NOTERING: standin?.NOTERING ?? '',
                  },
                };

                const newValues: FormUpdateAssociatonType[][] = [
                  [
                    updateObject,
                    {
                      associationName: 'Reserv',
                      operation: 'Ref',
                      id: standin?.Reserv,
                    },
                  ],
                  [
                    updateObject,
                    {
                      associationName: 'Attestratt',
                      operation: 'Ref',
                      id: reservContext.attestrattID ?? '',
                    },
                  ],
                ];
                businessDataFormContext?.update(newValues);

                businessDataFormContext?.saveForm(
                  mergeBusinessDataFormValues(
                    getFormUpdateValue(newValues, dataMergedWithChanges ?? {}),
                    {}
                  )
                );
              }
              reservContext.setDialogOpen(false);
            },
            buttonProps: {
              disabled: isSaveButtonDisabled,
              disabledReason: isSaveButtonDisabled
                ? getDisabledReasons(
                    standin,
                    reservContext.attestrattID,
                    props.attestratter
                  )
                : null,
            },
          },
        ]}
        onExited={() => {
          reservContext.setEditStandin(undefined);
          reservContext.setAttestrattID(null);
          setStandin(
            {} as AttestantQuery_node_TEDLRAttestant__Associations_Reserver_All
          );
          setCertifier(undefined);
        }}
      >
        <EDInputGroup>
          {reservData && reservContext.editStandin != null && standin ? (
            <>
              <EDTypography noWrap>
                <EDTypography component="span" bold>
                  Attestområde:
                </EDTypography>
                {(() => {
                  const currentAttestratt = props.attestratter.find(
                    attestratt => {
                      return (
                        attestratt?.id ===
                        reservData?._Associations?.Attestratt?.id
                      );
                    }
                  );

                  return `${
                    currentAttestratt?._Associations?.Attestomrade?.KOD
                      ? ` ${currentAttestratt?._Associations?.Attestomrade?.KOD}` +
                        ' - '
                      : ''
                  } ${
                    currentAttestratt?._Associations?.Attestomrade?.BENAMNING
                  }`;
                })()}
              </EDTypography>

              <EditableAttribute
                attribute={dateFromAttribute}
                collection={attestReservModel}
                disabled={false}
                editMode={true}
                fullWidth={isSmallScreen}
                onChange={newValue => {
                  updateFunction(businessDataFormContext)({
                    DATUM_FROM: newValue,
                  });
                }}
                required
                size={isSmallScreen ? 'large' : 'medium'}
                value={reservData?.DATUM_FROM ?? ''}
                paddingX={1}
              />

              <EditableAttribute
                attribute={dateToAttribute}
                collection={attestReservModel}
                disabled={false}
                editMode={true}
                fullWidth={isSmallScreen}
                onChange={newValue => {
                  updateFunction(businessDataFormContext)({
                    DATUM_TOM: newValue,
                  });
                }}
                size={isSmallScreen ? 'large' : 'medium'}
                value={reservData?.DATUM_TOM ?? ''}
                paddingX={1}
              />

              <EditableAttribute
                attribute={noteAttribute}
                collection={attestReservModel}
                disabled={false}
                editMode={true}
                multiline
                fullWidth={isSmallScreen}
                onChange={newValue => {
                  let value = newValue;
                  if (typeof value === 'string') {
                    value = value.trim();
                  }
                  updateFunction(businessDataFormContext)({
                    NOTERING: value,
                  });
                }}
                required
                size={!isSmallScreen ? 'large' : undefined}
                value={reservData?.NOTERING ?? ''}
                paddingX={1}
              />

              <EDAutoCompleteEdit<AllCertifiers_db_LRAttestratt_All>
                fullWidth
                paddingX={1}
                textFieldClassName={
                  !isSmallScreen ? classes.autocomplete : undefined
                }
                classes={{
                  label: !isSmallScreen ? classes.label : undefined,
                }}
                getOptionLabel={option =>
                  option.AttestNamnTitel ?? 'Kunde inte hitta namn på Attestant'
                }
                getOptionSelected={option => {
                  return option.id === certifier?.id;
                }}
                label="Reserv"
                onChange={option => {
                  if (option) {
                    if (!Array.isArray(option)) {
                      setCertifier(option);
                      businessDataFormContext?.update([
                        {
                          operation: 'Update_Values',
                          associationName: 'Reserver',
                          id: props.id,
                          values: getFormUpdateValue(
                            [],
                            dataMergedWithChanges ?? {}
                          ),
                        },
                        {
                          associationName: 'Reserv',
                          operation: 'Ref',
                          id: option.id,
                        },
                      ]);
                    }
                  }
                }}
                options={props.certifiers.filter(findCertifiers)}
                required
                value={
                  certifier ||
                  props.certifiers.find(
                    certifier =>
                      certifier._Associations?.Attestant?.SIGNATUR ===
                      standin._Associations?.Reserv?._Associations?.Attestant
                        ?.SIGNATUR
                  )
                }
                testId="reserv"
              />
            </>
          ) : (
            <>
              {props.attestratter?.length === 1 ? (
                <EDTypography noWrap>
                  <EDTypography component="span" bold>
                    Attestområde:
                  </EDTypography>
                  {(() => {
                    const currentAttestratt = props.attestratter[0];

                    return `${
                      currentAttestratt?._Associations?.Attestomrade?.KOD
                        ? ` ${currentAttestratt?._Associations?.Attestomrade?.KOD}` +
                          ' - '
                        : ''
                    } ${
                      currentAttestratt?._Associations?.Attestomrade?.BENAMNING
                    }`;
                  })()}
                </EDTypography>
              ) : (
                <EDAutoCompleteEdit
                  disabled={false}
                  classes={{
                    label: !isSmallScreen ? classes.label : undefined,
                  }}
                  fullWidth
                  paddingX={1}
                  getOptionLabel={option => {
                    const area = option?._Associations?.Attestomrade;
                    // Om man har flera attesträtter på samma område så ser man inte skillnad på dessa (det är fine)
                    return `${area?.KOD ? area?.KOD + ' -' : ''} ${
                      area?.BENAMNING
                    }`;
                  }}
                  label="Attestområde"
                  onChange={(
                    option:
                      | AttestantType__Associations_Attestratt_All
                      | null
                      | undefined
                  ) => {
                    if (option) {
                      reservContext.setAttestrattID(option.id ?? null);
                    }
                  }}
                  options={props.attestratter}
                  required
                  textFieldClassName={
                    !isSmallScreen ? classes.autocomplete : undefined
                  }
                  value={props.attestratter?.find(attestratt => {
                    return (
                      attestratt?.id ===
                      reservData?._Associations?.Attestratt?.id
                    );
                  })}
                />
              )}
              <EditableAttribute
                attribute={dateFromAttribute}
                collection={attestReservModel}
                disabled={false}
                editMode={true}
                fullWidth={isSmallScreen}
                onChange={newValue => {
                  setStandin({
                    ...standin,
                    DATUM_FROM: newValue,
                  });
                }}
                required
                size={isSmallScreen ? 'large' : 'medium'}
                value={standin?.DATUM_FROM}
                paddingX={1}
              />

              <EditableAttribute
                attribute={dateToAttribute}
                collection={attestReservModel}
                disabled={false}
                editMode={true}
                fullWidth={isSmallScreen}
                onChange={newValue => {
                  setStandin({ ...standin, DATUM_TOM: newValue });
                }}
                size={isSmallScreen ? 'large' : 'medium'}
                value={standin?.DATUM_TOM}
                paddingX={1}
              />

              <EditableAttribute
                attribute={noteAttribute}
                collection={attestReservModel}
                disabled={false}
                editMode={true}
                multiline
                fullWidth={isSmallScreen}
                onChange={newValue => {
                  setStandin({ ...standin, NOTERING: newValue });
                }}
                required
                size={!isSmallScreen ? 'large' : undefined}
                value={standin?.NOTERING}
                paddingX={1}
              />

              <EDAutoCompleteEdit<AllCertifiers_db_LRAttestratt_All>
                fullWidth
                paddingX={1}
                textFieldClassName={
                  !isSmallScreen ? classes.autocomplete : undefined
                }
                classes={{
                  label: !isSmallScreen ? classes.label : undefined,
                }}
                disabled={reservFieldDisabled}
                getOptionLabel={option =>
                  option.AttestNamnTitel ?? 'Kunde inte hitta namn på Attestant'
                }
                getOptionSelected={option => {
                  return option.id === certifier?.id;
                }}
                label="Reserv"
                onChange={option => {
                  if (option) {
                    if (!Array.isArray(option)) {
                      setCertifier(option);
                      setStandin({
                        ...standin,
                        Reserv: option.id,
                      });
                    }
                  }
                }}
                options={props.certifiers.filter(findCertifiers)}
                required
                value={
                  (certifier ||
                    props.certifiers.find(
                      certifier =>
                        certifier._Associations?.Attestant?.SIGNATUR ===
                        standin?._Associations?.Reserv?._Associations?.Attestant
                          ?.SIGNATUR
                    )) ??
                  null
                }
                testId="reserv"
              />
            </>
          )}
        </EDInputGroup>
      </Dialog>
    )
  );
};

export default Reserv;
